import type { ClientProjectActivity, EmailSortDirection } from '@packages/types';
import { getLocalDayKey } from './group-by-day';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DayGroup = {
  /** Calendar day key in YYYY-MM-DD format. */
  dayKey: string;
  /** Canonical ISO string for the start of the day (noon local time, used for label computation). */
  dateISO: string;
  /** Non-archived activities for this day, sorted by createdAt descending. */
  activities: ClientProjectActivity[];
};

// ---------------------------------------------------------------------------
// Exported functions
// ---------------------------------------------------------------------------

/**
 * Normalise an activity date value to a YYYY-MM-DD calendar-day key.
 *
 * The read API always returns `activity.date` as a full ISO instant - the route
 * serialises every activity via `activity.date.toISOString()` - and web-app-recorded
 * activities encode their user-picked calendar day as a UTC-midnight instant. Full
 * instants are localised to the browser's timezone via `getLocalDayKey`, so an email
 * received at 01:21 SAST (`23:21:00.000Z` the previous UTC day) buckets under its own
 * SAST calendar day rather than the UTC one. For UTC-midnight-encoded calendar days
 * this is a deliberate trade-off: correct in UTC+ zones (the SA market), a day early
 * in zones west of UTC.
 *
 * The bare `YYYY-MM-DD` branch defends `dayHeaderLabel`'s documented input contract,
 * which also accepts bare day keys: a bare date is already the key and must not
 * round-trip through `new Date()`, because date-only strings parse as UTC midnight
 * and would shift back a day west of UTC.
 */
export function toDateKey(dateValue: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  return getLocalDayKey(new Date(dateValue));
}

/**
 * Group a flat list of activities into calendar-day buckets.
 *
 * - Excludes archived activities (those with a non-null `archivedAt`).
 * - Days are ordered by `options.dayOrder` (default `'desc'`, most recent
 *   first). Pass `'asc'` for upcoming/scheduled work where soonest-first
 *   reads more naturally.
 * - Within each day, activities are sorted by `createdAt` descending. The
 *   intra-day order is intentionally constant: even for upcoming work, the
 *   time-of-day component of `activity.date` is rarely meaningful, so
 *   "most-recently-logged first" is the consistent rule.
 * - Returns an empty array when the input is empty or all activities are
 *   archived.
 */
export function groupActivitiesByDay(
  activities: ClientProjectActivity[],
  options?: { dayOrder?: EmailSortDirection },
): DayGroup[] {
  const dayOrder = options?.dayOrder ?? 'desc';
  const visible = activities.filter((a) => a.archivedAt === null);

  if (visible.length === 0) {
    return [];
  }

  // Accumulate activities by calendar day key.
  const groupMap = new Map<string, ClientProjectActivity[]>();
  for (const activity of visible) {
    const key = toDateKey(activity.date);
    const existing = groupMap.get(key);
    if (existing !== undefined) {
      existing.push(activity);
    } else {
      groupMap.set(key, [activity]);
    }
  }

  // Sort day keys per dayOrder. localeCompare on YYYY-MM-DD keys is
  // chronological, so flipping the operand order flips the direction.
  const sortedKeys = Array.from(groupMap.keys()).sort((a, b) =>
    dayOrder === 'asc' ? a.localeCompare(b) : b.localeCompare(a),
  );

  return sortedKeys.map((dayKey): DayGroup => {
    const dayActivities = groupMap.get(dayKey) ?? [];

    // Sort within day: most recently recorded first. We sort by `createdAt`
    // (record-insertion time) rather than `date` because `date` is typically
    // just the calendar day - users rarely record the time of day at which
    // the work happened, so the time portion of `date` is not meaningful.
    const sorted = [...dayActivities].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );

    return {
      dayKey,
      // Use noon local time so that Date construction is not affected by
      // timezone offsets that might shift the calendar day.
      dateISO: `${dayKey}T12:00:00`,
      activities: sorted,
    };
  });
}

/**
 * Returns true when an activity is not archived and its date falls strictly
 * after the current instant, meaning it is scheduled for a future event and
 * is not yet billable.
 *
 * Both `archivedAt` and `date` are accepted as ISO strings or null, matching
 * the shape returned by the API's `ClientProjectActivity` schema.
 */
export function isUpcoming(activity: { archivedAt: string | null; date: string | null }): boolean {
  return activity.archivedAt === null && activity.date !== null && new Date(activity.date) > new Date();
}

/**
 * Produce a human-readable label for a diary day header.
 *
 * - Returns `"Today"` when `dateISO` falls on the same calendar day as `now`.
 * - Returns `"Yesterday"` for the preceding calendar day.
 * - Otherwise returns a formatted string in `en-ZA` locale, e.g.
 *   `"Mon, 24 March 2026"`.
 *
 * Comparison is by calendar day in local time so that activities logged
 * at 23:59 and 00:01 on adjacent days do not collapse into one group.
 *
 * @param dateISO - An ISO string or YYYY-MM-DD value representing the day.
 * @param now - The reference instant for "today". Defaults to `new Date()`.
 */
export function dayHeaderLabel(dateISO: string, now: Date = new Date()): string {
  const dayKey = toDateKey(dateISO);

  const todayKey = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

  if (dayKey === todayKey) {
    return 'Today';
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = [
    yesterday.getFullYear(),
    String(yesterday.getMonth() + 1).padStart(2, '0'),
    String(yesterday.getDate()).padStart(2, '0'),
  ].join('-');

  if (dayKey === yesterdayKey) {
    return 'Yesterday';
  }

  // Use noon local time to avoid day-boundary shifts when constructing
  // a Date from a bare YYYY-MM-DD string.
  const date = new Date(`${dayKey}T12:00:00`);

  // Intl.DateTimeFormat with en-ZA produces e.g. "Mon, 24 March 2026".
  // The formatter may append a comma after the weekday abbreviation in some
  // environments - normalise by removing any trailing comma on the weekday
  // segment.
  const formatted = new Intl.DateTimeFormat('en-ZA', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);

  // en-ZA can produce "Mon, 24 March 2026" or "Mon 24 March 2026" depending
  // on the runtime's ICU data version. Normalise to the comma form:
  // "Mon, 24 March 2026". Split on first space after the weekday (3-4 chars),
  // then rejoin with ", " to guarantee a consistent separator.
  const commaIndex = formatted.indexOf(',');
  if (commaIndex !== -1) {
    // Already has a comma - return as-is (standard form).
    return formatted;
  }

  // No comma present - insert one after the first word (weekday abbreviation).
  const spaceIndex = formatted.indexOf(' ');
  if (spaceIndex === -1) {
    return formatted;
  }
  return `${formatted.slice(0, spaceIndex)}, ${formatted.slice(spaceIndex + 1)}`;
}
